import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Eye,
  FilterX,
  Info,
  Loader2,
  RefreshCw,
  RotateCcw,
  ServerCog,
  XCircle,
} from "lucide-react";
import api from "../../api";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

type JobStatus = "PENDING" | "PROCESSING" | "SUCCESS" | "FAILED_RETRYABLE" | "FAILED_FINAL" | "SKIPPED";
type JobType = "PAYMENT_LINK_RECOVERY" | "PAYMENT_STATUS_POLL";

interface EventOption {
  id: string;
  name: string;
}

interface SummaryResponse {
  byStatus: Record<JobStatus, number>;
  byType: Record<JobType, number>;
  readyToProcess: number;
  failedFinal: number;
  processingStale: number;
}

interface MonitorJob {
  id: string;
  jobType: JobType;
  status: JobStatus;
  attemptCount: number;
  maxAttempts: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  lockedAt: string | null;
  lockedBy: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  lastErrorPreview: string | null;
  operationLabel: string | null;
  registration: {
    id: string;
    folio: string;
    status: string;
    role: string;
    participantName: string;
    emailMasked: string | null;
    tipoParticipante: string;
    participationMode: string | null;
    syswebReferenceMasked: string | null;
    braceletCodeMasked: string | null;
    createdAt: string;
  };
  event: {
    id: string;
    name: string;
    shortName: string | null;
  };
}

interface MonitorListResponse {
  items: MonitorJob[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface MonitorJobDetail extends MonitorJob {
  lastError: string | null;
  timeline: Array<{ label: string; at: string }>;
}

interface SyswebMonitorPageProps {
  canView: boolean;
  selectedEventId: string;
  availableEvents: EventOption[];
}

const statusLabels: Record<JobStatus, string> = {
  PENDING: "Pendiente",
  PROCESSING: "En proceso",
  SUCCESS: "Exitoso",
  FAILED_RETRYABLE: "Reintentable",
  FAILED_FINAL: "Fallido",
  SKIPPED: "Omitido",
};

const jobTypeLabels: Record<JobType, string> = {
  PAYMENT_LINK_RECOVERY: "Recuperación de link",
  PAYMENT_STATUS_POLL: "Validación de pago",
};

const statusClasses: Record<JobStatus, string> = {
  PENDING: "border-blue-200 bg-blue-50 text-blue-700",
  PROCESSING: "border-amber-200 bg-amber-50 text-amber-700",
  SUCCESS: "border-emerald-200 bg-emerald-50 text-emerald-700",
  FAILED_RETRYABLE: "border-orange-200 bg-orange-50 text-orange-700",
  FAILED_FINAL: "border-red-200 bg-red-50 text-red-700",
  SKIPPED: "border-gray-200 bg-gray-50 text-gray-600",
};

const jobTypeClasses: Record<JobType, string> = {
  PAYMENT_LINK_RECOVERY: "border-indigo-200 bg-indigo-50 text-indigo-700",
  PAYMENT_STATUS_POLL: "border-sky-200 bg-sky-50 text-sky-700",
};

const primaryButtonClass = "bg-[#1e3a5f] text-white hover:bg-[#0f2744] hover:text-white disabled:text-white";

function formatJobDiagnostic(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = value.trim();
  if (normalized === "MANUAL_PAYMENT_CONFIRMED") return "Omitido por pago manual";
  if (
    normalized === "REGISTRATION_ALREADY_RESOLVED" ||
    normalized === "REGISTRATION_ALREADY_RESOLVED_MANUALLY"
  ) {
    return "Omitido: registro ya resuelto";
  }
  if (normalized === "RESOLVED_BY_SYSWEB_CALLBACK") return "Omitido: resuelto por callback SYSWEB";
  return normalized;
}

function formatDate(value: string | null | undefined, includeTime = true): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: includeTime ? undefined : "numeric",
    hour: includeTime ? "2-digit" : undefined,
    minute: includeTime ? "2-digit" : undefined,
  }).format(date);
}

function roleLabel(role: string): string {
  return role === "SPEAKER" ? "Ponente" : "Asistente";
}

function registrationStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    PENDING: "Pendiente",
    PENDING_REVIEW: "En revisión",
    APPROVED_TO_PAY: "Aprobado a pago",
    REJECTED: "Rechazado",
    PAID: "Pagado",
    MATERIAL_UPLOADED: "Material cargado",
    CREDENTIAL_ISSUED: "Credencial emitida",
  };
  return labels[status] || status;
}

function StatusBadge({ status }: { status: JobStatus }) {
  return <Badge variant="outline" className={statusClasses[status]}>{statusLabels[status]}</Badge>;
}

function JobTypeBadge({ jobType }: { jobType: JobType }) {
  return <Badge variant="outline" className={jobTypeClasses[jobType]}>{jobTypeLabels[jobType]}</Badge>;
}

function KpiCard({
  title,
  value,
  icon: Icon,
  tone,
  code,
}: {
  title: string;
  value: number;
  icon: typeof Clock3;
  tone: string;
  code: string;
}) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-full ${tone}`}>
            <Icon className="h-5 w-5" />
          </div>
          <Badge variant="outline" className="border-gray-200 bg-white text-[10px] text-gray-600">{code}</Badge>
        </div>
        <div className="mt-5">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-1 text-3xl font-bold text-[#0f2744]">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function SyswebMonitorPage({ canView, selectedEventId, availableEvents }: SyswebMonitorPageProps) {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [jobs, setJobs] = useState<MonitorJob[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [eventId, setEventId] = useState<string>(selectedEventId || "ALL");
  const [jobType, setJobType] = useState<string>("ALL");
  const [status, setStatus] = useState<string>("ALL");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MonitorJobDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  useEffect(() => {
    setEventId(selectedEventId || "ALL");
  }, [selectedEventId]);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page,
      pageSize,
      sortBy: "updatedAt",
      sortDirection: "desc",
    };
    if (eventId !== "ALL") params.eventId = eventId;
    if (jobType !== "ALL") params.jobType = jobType;
    if (status !== "ALL") params.status = status;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (q.trim().length >= 3) params.q = q.trim();
    return params;
  }, [dateFrom, dateTo, eventId, jobType, page, pageSize, q, status]);

  const loadMonitor = useCallback(async () => {
    if (!canView) return;
    setIsLoading(true);
    setError(null);
    try {
      const summaryResponse = await api.get<SummaryResponse>("/admin/sysweb/jobs/summary");
      const listResponse = await api.get<MonitorListResponse>("/admin/sysweb/jobs", {
        params: queryParams,
        headers: { "X-Passive-Request": "true" },
      });
      setSummary(summaryResponse.data);
      setJobs(listResponse.data.items);
      setTotal(listResponse.data.total);
      setTotalPages(listResponse.data.totalPages);
    } catch {
      setError("No se pudo cargar el monitor.");
    } finally {
      setIsLoading(false);
    }
  }, [canView, queryParams]);

  useEffect(() => {
    loadMonitor();
  }, [loadMonitor]);

  const loadDetail = async (jobId: string) => {
    setDetailId(jobId);
    setIsDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get<MonitorJobDetail>(`/admin/sysweb/jobs/${jobId}`, {
        headers: { "X-Passive-Request": "true" },
      });
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const clearFilters = () => {
    setEventId(selectedEventId || "ALL");
    setJobType("ALL");
    setStatus("ALL");
    setQ("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  if (!canView) {
    return (
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="flex min-h-[360px] flex-col items-center justify-center p-8 text-center">
          <AlertCircle className="mb-4 h-10 w-10 text-amber-500" />
          <h2 className="text-xl font-bold text-[#0f2744]">Acceso restringido</h2>
          <p className="mt-2 max-w-md text-sm text-gray-600">No tienes permisos para visualizar el Monitor SYSWEB.</p>
        </CardContent>
      </Card>
    );
  }

  const hasShortSearch = q.trim().length > 0 && q.trim().length < 3;
  const hasAnyJobs = total > 0 || jobs.length > 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#0f2744]">Monitor SYSWEB</h1>
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Solo lectura</Badge>
          </div>
          <p className="mt-1 text-sm text-gray-600">Seguimiento de jobs de recuperación de links y validación de pagos.</p>
        </div>
        <Button onClick={loadMonitor} disabled={isLoading} className={primaryButtonClass}>
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Actualizar
        </Button>
      </div>

      <Alert className="border-blue-200 bg-blue-50 text-blue-900">
        <Info className="h-4 w-4" />
        <AlertDescription>
          Este monitor es de solo lectura. No ejecuta workers, no modifica registros y no consulta directamente a SYSWEB.
        </AlertDescription>
      </Alert>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        <KpiCard title="Pendientes" value={summary?.byStatus.PENDING ?? 0} icon={Clock3} tone="bg-blue-100 text-blue-700" code="PENDING" />
        <KpiCard title="En proceso" value={summary?.byStatus.PROCESSING ?? 0} icon={RotateCcw} tone="bg-amber-100 text-amber-700" code="PROCESSING" />
        <KpiCard title="Exitosos" value={summary?.byStatus.SUCCESS ?? 0} icon={CheckCircle2} tone="bg-emerald-100 text-emerald-700" code="SUCCESS" />
        <KpiCard title="Fallidos" value={summary?.failedFinal ?? 0} icon={XCircle} tone="bg-red-100 text-red-700" code="FAILED_FINAL" />
        <KpiCard title="Omitidos" value={summary?.byStatus.SKIPPED ?? 0} icon={AlertCircle} tone="bg-gray-100 text-gray-600" code="SKIPPED" />
        <KpiCard title="Listos para procesar" value={summary?.readyToProcess ?? 0} icon={ServerCog} tone="bg-sky-100 text-sky-700" code="READY" />
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-2">
              <Label>Evento</Label>
              <Select value={eventId} onValueChange={(value) => { setEventId(value); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  {availableEvents.map((event) => <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de job</Label>
              <Select value={jobType} onValueChange={(value) => { setJobType(value); setPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="PAYMENT_STATUS_POLL">Validación de pago</SelectItem>
                  <SelectItem value="PAYMENT_LINK_RECOVERY">Recuperación de link</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select value={status} onValueChange={(value) => { setStatus(value); setPage(1); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  {Object.entries(statusLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Buscar</Label>
              <Input
                value={q}
                onChange={(event) => { setQ(event.target.value); setPage(1); }}
                placeholder="Folio o referencia"
              />
              {hasShortSearch && <p className="text-xs text-amber-700">Escribe al menos 3 caracteres para buscar.</p>}
            </div>
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setPage(1); }} />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setPage(1); }} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={clearFilters}>
              <FilterX className="mr-2 h-4 w-4" />
              Limpiar filtros
            </Button>
            <Button onClick={loadMonitor} className={primaryButtonClass}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#0f2744]">Jobs recientes</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
              <AlertCircle className="mb-3 h-10 w-10 text-red-500" />
              <h3 className="text-lg font-semibold text-[#0f2744]">No se pudo cargar el monitor</h3>
              <p className="mt-2 max-w-md text-sm text-gray-600">Intenta actualizar la información. Si el problema persiste, revisa los logs del servidor.</p>
              <Button className={`mt-4 ${primaryButtonClass}`} onClick={loadMonitor}>Reintentar carga</Button>
            </div>
          ) : isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-center">
              <ServerCog className="mb-3 h-10 w-10 text-blue-500" />
              <h3 className="text-lg font-semibold text-[#0f2744]">{hasAnyJobs ? "No se encontraron resultados" : "Sin jobs SYSWEB registrados"}</h3>
              <p className="mt-2 max-w-md text-sm text-gray-600">
                {hasAnyJobs
                  ? "No hay jobs que coincidan con los filtros seleccionados."
                  : "Cuando se generen registros pendientes de pago o recuperaciones de link, aparecerán aquí."}
              </p>
              <div className="mt-4 flex gap-2">
                {hasAnyJobs && <Button variant="outline" onClick={clearFilters}>Limpiar filtros</Button>}
                <Button className={primaryButtonClass} onClick={loadMonitor}>Actualizar</Button>
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Participante</TableHead>
                    <TableHead>Evento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Estado job</TableHead>
                    <TableHead>Estado registro</TableHead>
                    <TableHead>Intentos</TableHead>
                    <TableHead>Próximo intento</TableHead>
                    <TableHead>Último intento</TableHead>
                    <TableHead>Actualizado</TableHead>
                    <TableHead>Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="font-medium text-[#0f2744]">{job.registration.folio}</TableCell>
                      <TableCell>{job.registration.participantName}</TableCell>
                      <TableCell className="max-w-[220px] truncate">{job.event.shortName || job.event.name}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <JobTypeBadge jobType={job.jobType} />
                          {job.operationLabel && (
                            <p className="text-xs font-medium text-blue-700">{job.operationLabel}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={job.status} /></TableCell>
                      <TableCell><Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">{registrationStatusLabel(job.registration.status)}</Badge></TableCell>
                      <TableCell>{job.attemptCount}/{job.maxAttempts}</TableCell>
                      <TableCell>{formatDate(job.nextAttemptAt)}</TableCell>
                      <TableCell>{formatDate(job.lastAttemptAt)}</TableCell>
                      <TableCell>{formatDate(job.updatedAt)}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => loadDetail(job.id)}>
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">Mostrando {jobs.length} de {total} jobs</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => Math.max(value - 1, 1))}>Anterior</Button>
                  <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>Siguiente</Button>
                  <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                    <SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Alert className="border-blue-200 bg-white text-blue-900">
        <Info className="h-4 w-4" />
        <AlertDescription>Los correos, tokens, URLs de pago y payloads SYSWEB no se muestran por seguridad.</AlertDescription>
      </Alert>

      <Sheet open={Boolean(detailId)} onOpenChange={(open) => { if (!open) { setDetailId(null); setDetail(null); } }}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle className="text-xl text-[#0f2744]">Detalle del job SYSWEB</SheetTitle>
            <SheetDescription>Información de solo lectura</SheetDescription>
          </SheetHeader>
          {isDetailLoading ? (
            <div className="flex min-h-[320px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
            </div>
          ) : detail ? (
            <div className="space-y-4 px-4 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#0f2744]">{detail.registration.folio}</span>
                <JobTypeBadge jobType={detail.jobType} />
                <StatusBadge status={detail.status} />
              </div>

              <DetailSection title="1. Información del job">
                <DetailGrid rows={[
                  ["ID job", detail.id],
                  ["Tipo", jobTypeLabels[detail.jobType]],
                  ["Operacion", detail.operationLabel || "-"],
                  ["Estado", statusLabels[detail.status]],
                  ["Intentos", `${detail.attemptCount}/${detail.maxAttempts}`],
                  ["Creado", formatDate(detail.createdAt)],
                  ["Actualizado", formatDate(detail.updatedAt)],
                  ["Próximo intento", formatDate(detail.nextAttemptAt)],
                  ["Último intento", formatDate(detail.lastAttemptAt)],
                  ["Locked by", detail.lockedBy || "-"],
                  ["Locked at", formatDate(detail.lockedAt)],
                ]} />
              </DetailSection>

              <DetailSection title="2. Registro asociado">
                <DetailGrid rows={[
                  ["Folio", detail.registration.folio],
                  ["Estado del registro", registrationStatusLabel(detail.registration.status)],
                  ["Evento", detail.event.name],
                  ["Participante", detail.registration.participantName],
                  ["Correo", detail.registration.emailMasked || "-"],
                  ["Tipo", roleLabel(detail.registration.role)],
                  ["Modalidad", detail.registration.participationMode || detail.registration.tipoParticipante],
                  ["Referencia", detail.registration.syswebReferenceMasked || "-"],
                  ["Pulsera", detail.registration.braceletCodeMasked || "-"],
                ]} />
              </DetailSection>

              <div className="grid gap-4 lg:grid-cols-2">
                <DetailSection title="3. Timeline operativo">
                  <div className="space-y-3">
                    {detail.timeline.map((item, index) => (
                      <div key={`${item.label}-${index}`} className="flex gap-3">
                        <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-[#0f2744]">{item.label}</p>
                          <p className="text-xs text-gray-500">{formatDate(item.at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </DetailSection>
                <DetailSection title="4. Error / diagnóstico">
                  {detail.lastError ? (
                    <pre className={`max-h-56 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-xs ${
                      detail.status === "SKIPPED"
                        ? "border-gray-200 bg-gray-50 text-gray-700"
                        : "border-red-100 bg-red-50 text-red-800"
                    }`}>{formatJobDiagnostic(detail.lastError)}</pre>
                  ) : (
                    <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">No hay errores registrados para este job.</div>
                  )}
                </DetailSection>
              </div>

              <DetailSection title="5. Advertencia de seguridad">
                <p className="text-sm text-gray-600">Este panel no muestra tokens, URLs de pago completas ni payloads crudos de SYSWEB.</p>
              </DetailSection>
            </div>
          ) : (
            <div className="px-4 text-sm text-gray-600">No se pudo cargar el detalle del job.</div>
          )}
          <SheetFooter>
            <Button variant="outline" onClick={() => { setDetailId(null); setDetail(null); }}>Cerrar</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#0f2744]">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DetailGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>
          <p className="mt-1 break-words text-sm text-gray-800">{value}</p>
        </div>
      ))}
    </div>
  );
}
